export const TERMS_VERSION = "2026-07-17";
const TERMS_ACCEPTANCE_STORAGE_PREFIX = "xima_terms_acceptance";
const inMemoryAcceptedTerms = new Set();

const normalizeAcceptanceSubject = (value) =>
  String(value || "anonymous")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.@:-]+/g, "_");

const resolveTermsAcceptanceStorageKey = (subject = "anonymous") =>
  `${TERMS_ACCEPTANCE_STORAGE_PREFIX}:${TERMS_VERSION}:${normalizeAcceptanceSubject(subject)}`;

const getTermsStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
};

export const hasAcceptedTerms = (subject = "anonymous") => {
  if (typeof window === "undefined") return false;
  const key = resolveTermsAcceptanceStorageKey(subject);
  if (inMemoryAcceptedTerms.has(key)) return true;
  const storage = getTermsStorage();
  if (!storage) return false;
  try {
    return storage.getItem(key) === "1";
  } catch (error) {
    return false;
  }
};

export const markTermsAccepted = (subject = "anonymous") => {
  if (typeof window === "undefined") return false;
  const key = resolveTermsAcceptanceStorageKey(subject);
  inMemoryAcceptedTerms.add(key);
  const storage = getTermsStorage();
  if (!storage) return true;
  try {
    storage.setItem(key, "1");
  } catch (error) {
    // In private or embedded browsers storage can be unavailable; memory keeps this session flowing.
  }
  return true;
};

export const resolveUserTermsSubject = (user) => {
  const candidates = [
    user?.id,
    user?._id,
    user?.userId,
    user?.username,
    user?.loginName,
    user?.email,
    user?.contact,
    user?.sub,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate || "").trim().toLowerCase();
    if (normalized) return `user:${normalized}`;
  }

  return "user:unknown";
};

export const TERMS_MODAL_COPY = {
  title: {
    es: "Términos y Condiciones de Uso",
    en: "Terms and Conditions",
    ru: "Условия использования",
    de: "Nutzungsbedingungen",
  },
  guestIntro: {
    es: 'Al seleccionar "Jugar sin registrarme", confirmas que has leído, entendido y aceptado estos términos.',
    en: 'By selecting "Play without registering," you confirm that you have read, understood, and accepted these terms.',
    ru: 'Выбирая «Играть без регистрации», вы подтверждаете, что прочитали, поняли и приняли эти условия.',
    de: 'Mit "Ohne Registrierung spielen" bestätigen Sie, dass Sie diese Bedingungen gelesen, verstanden und akzeptiert haben.',
  },
  registerIntro: {
    es: 'Al seleccionar "REGISTRAR", confirmas que has leído, entendido y aceptado estos términos.',
    en: 'By selecting "REGISTER", you confirm that you have read, understood, and accepted these terms.',
    ru: 'Выбирая «REGISTER», вы подтверждаете, что прочитали, поняли и приняли эти условия.',
    de: 'Mit "REGISTRIEREN" bestätigen Sie, dass Sie diese Bedingungen gelesen, verstanden und akzeptiert haben.',
  },
  authenticatedIntro: {
    es: "Para continuar, debes leer y aceptar estos Términos y Condiciones.",
    en: "To continue, you must read and accept these Terms and Conditions.",
    ru: "Чтобы продолжить, необходимо прочитать и принять эти Условия.",
    de: "Um fortzufahren, musst du diese Nutzungsbedingungen lesen und akzeptieren.",
  },
  acceptLabelGuest: {
    es: "Acepto los Términos y Condiciones para jugar como invitado.",
    en: "I accept the Terms and Conditions to play as a guest.",
    ru: "Я принимаю Условия, чтобы играть как гость.",
    de: "Ich akzeptiere die Nutzungsbedingungen für den Gastzugang.",
  },
  acceptLabelRegister: {
    es: "Acepto los Términos y Condiciones para crear mi cuenta.",
    en: "I accept the Terms and Conditions to create my account.",
    ru: "Я принимаю Условия для создания учетной записи.",
    de: "Ich akzeptiere die Nutzungsbedingungen, um mein Konto zu erstellen.",
  },
  acceptLabelAuthenticated: {
    es: "Acepto los Términos y Condiciones para continuar.",
    en: "I accept the Terms and Conditions to continue.",
    ru: "Я принимаю Условия, чтобы продолжить.",
    de: "Ich akzeptiere die Nutzungsbedingungen, um fortzufahren.",
  },
  cancel: {
    es: "Cancelar",
    en: "Cancel",
    ru: "Отмена",
    de: "Abbrechen",
  },
  accept: {
    es: "Aceptar y continuar",
    en: "Accept",
    ru: "Принять и продолжить",
    de: "Akzeptieren und fortfahren",
  },
  languageLabel: {
    es: "Idioma",
    en: "Language",
    ru: "Язык",
    de: "Sprache",
  },
};

export const TERMS_CONTENT_MARKDOWN = {
  es: `# TÉRMINOS Y CONDICIONES DE USO DE XIMA

**Última actualización:** [Fecha]

Los presentes Términos y Condiciones regulan el acceso y uso del juego digital **XIMA**, disponible a través de los sitios web oficiales:

* **https://juegoxima.com**
* **https://playxima.com**

Al crear una cuenta, acceder o utilizar cualquiera de estos sitios web, el usuario declara haber leído, comprendido y aceptado estos Términos y Condiciones.

## 1. Información General

XIMA es un juego digital de estrategia y entretenimiento desarrollado bajo la marca registrada **MUIÑO**.

Los dominios **juegoxima.com** y **playxima.com** forman parte del ecosistema oficial de XIMA.

El servicio puede encontrarse en desarrollo continuo, por lo que algunas funciones podrán modificarse, suspenderse temporalmente o presentar errores técnicos.

## 2. Uso del Servicio

El uso de XIMA es voluntario y se realiza bajo responsabilidad del usuario.

El servicio se proporciona **“TAL CUAL” (“AS IS”)**, sin garantías de disponibilidad permanente, funcionamiento ininterrumpido, ausencia de errores, compatibilidad con todos los dispositivos o conservación permanente de datos, progreso o partidas.

En la máxima medida permitida por la ley, los responsables de XIMA no serán responsables por pérdidas de información, progreso, partidas, interrupciones del servicio, daños indirectos o cualquier consecuencia derivada del uso o imposibilidad de uso del servicio.

## 3. Cuentas de Usuario

Para acceder a las funciones de XIMA es necesario crear una cuenta de usuario.

El usuario declara que la información proporcionada durante el registro es verdadera, completa y actualizada.

El usuario es responsable de mantener la confidencialidad de sus credenciales de acceso y de toda actividad realizada desde su cuenta.

XIMA podrá requerir procesos adicionales de verificación para acceder a determinadas funciones, participar en eventos, solicitar beneficios o reclamar recompensas promocionales.

## 4. Conducta del Usuario

El usuario se compromete a utilizar XIMA de forma legal, responsable y respetuosa.

Queda prohibido:

* usar trampas, hacks o software no autorizado;
* utilizar bots o automatizaciones no autorizadas;
* manipular el funcionamiento del juego;
* explotar errores o bugs;
* vulnerar la seguridad del sistema;
* intentar acceder a áreas restringidas;
* realizar ingeniería inversa;
* crear cuentas falsas o múltiples con fines fraudulentos;
* suplantar la identidad de otros usuarios;
* acosar, amenazar o perjudicar a otros jugadores.

## 5. Medidas de Seguridad

XIMA podrá suspender, limitar o eliminar cuentas, cancelar partidas, retirar recompensas, bloquear accesos o adoptar otras medidas cuando existan indicios de fraude, abuso, actividad sospechosa o incumplimiento de estos Términos.

Estas medidas podrán aplicarse sin previo aviso cuando sea necesario para proteger la seguridad, integridad y funcionamiento del servicio.

## 6. Naturaleza del Juego

XIMA es un juego de estrategia, lógica y habilidad.

La plataforma puede incluir partidas entre usuarios, torneos, desafíos, puzzles, modos de práctica y cajas de arena.

Algunas modalidades pueden incorporar elementos aleatorios, como lanzamientos de dados u otros mecanismos variables.

Estos elementos se utilizan para generar escenarios dinámicos y situaciones estratégicas diferentes, pero no determinan automáticamente el resultado de las partidas.

El desempeño del jugador depende principalmente de su habilidad, razonamiento, planificación, experiencia, adaptación y toma de decisiones.

## 7. Monedas Virtuales

XIMA puede utilizar monedas virtuales, puntos, créditos, experiencia, bonificaciones u otros elementos digitales dentro del ecosistema del juego.

Estos elementos:

* no representan dinero real;
* no son moneda electrónica;
* no son criptomonedas;
* no son activos financieros;
* no constituyen inversiones;
* no poseen valor monetario fuera de XIMA;
* no pueden canjearse por dinero en efectivo;
* no pueden transferirse a cuentas bancarias.

Su uso está limitado exclusivamente a la experiencia interactiva, competitiva y de progreso dentro del juego.

## 8. Recompensas Promocionales

XIMA podrá ofrecer, de forma limitada y promocional, productos oficiales, artículos promocionales, tarjetas de regalo, premios físicos, contenido digital u otros beneficios especiales.

Estas recompensas tienen carácter exclusivamente promocional y de entretenimiento.

No constituyen salarios, inversiones, activos financieros, compensaciones monetarias, apuestas ni obligaciones económicas permanentes.

Las recompensas estarán sujetas a disponibilidad, validación antifraude, verificación de cuenta, restricciones geográficas, políticas internas y cumplimiento de estos Términos.

Solo las cuentas registradas que cumplan con los requisitos establecidos por XIMA podrán acceder, cuando corresponda, a recompensas promocionales, beneficios especiales o artículos físicos.

XIMA podrá revisar, limitar, aprobar, rechazar, modificar, suspender o cancelar cualquier recompensa, saldo virtual, solicitud promocional o cuenta de usuario en casos de fraude, abuso, actividad sospechosa, multicuentas, uso automatizado no autorizado, explotación de errores o incumplimiento de estos Términos.

## 9. Ausencia de Apuestas

XIMA no constituye un casino, plataforma de apuestas, lotería ni servicio de juego de azar con dinero real.

Los usuarios no realizan apuestas monetarias ni arriesgan dinero dentro de la plataforma.

La participación en partidas, torneos, desafíos o puzzles no implica pérdidas económicas para los usuarios en caso de derrota, fallo o bajo desempeño.

Las recompensas promocionales, cuando existan, forman parte de campañas de entretenimiento y promoción vinculadas al ecosistema XIMA.

## 10. Propiedad Intelectual

Todo el contenido de XIMA, incluyendo código fuente, diseño del juego, mecánicas, reglas, textos, libros, imágenes, modelos 3D, interfaces, sonidos, música, animaciones, vídeos, documentación, bases de datos, logotipos, nombres comerciales y elementos visuales, está protegido por derechos de autor y leyes de propiedad intelectual.

Queda prohibida la copia, reproducción, distribución, modificación, extracción, comercialización o uso no autorizado de dicho contenido sin autorización previa y por escrito del titular de los derechos.

## 11. Marca Registrada

La marca **MUIÑO**, así como los signos distintivos asociados a XIMA, están legalmente protegidos.

Queda prohibida su copia, reproducción, distribución, modificación, comercialización o uso sin autorización expresa y por escrito del titular de los derechos.

## 12. Disponibilidad del Servicio

XIMA podrá suspender temporal o definitivamente el servicio, total o parcialmente, por motivos técnicos, mantenimiento, actualizaciones, seguridad, fallos, fuerza mayor o cualquier otra circunstancia necesaria para preservar el funcionamiento del proyecto.

No se garantiza disponibilidad continua o permanente del servicio.

## 13. Modificaciones

XIMA podrá modificar estos Términos y Condiciones en cualquier momento.

También podrá modificar reglas, modos de juego, recompensas, monedas virtuales, funcionalidades, diseño, contenido o cualquier elemento del servicio.

Las modificaciones entrarán en vigor desde su publicación en los sitios web oficiales.

El uso continuado de XIMA después de la publicación de cambios implica la aceptación de dichos cambios.

## 14. Privacidad

El uso de XIMA puede implicar el tratamiento de datos necesarios para el funcionamiento del servicio, incluyendo información de registro, estadísticas de juego, identificadores técnicos, direcciones IP, registros de actividad y datos relacionados con seguridad, soporte y prevención de fraude.

El tratamiento de estos datos se realizará conforme a la Política de Privacidad publicada en los sitios web oficiales.

### Uso de micrófono, audio y funciones de voz

XIMA puede solicitar acceso al micrófono y procesar audio, comandos de voz, transcripciones o señales de voz únicamente cuando el usuario active o permita funciones que lo requieran, incluyendo control de voz, respuestas del juego, chat o comunicación de voz, accesibilidad, mejoras de la experiencia de juego y funcionamiento general de la página web.

El acceso al micrófono depende del permiso otorgado por el usuario en su navegador, sistema operativo o dispositivo. El usuario puede denegar, retirar o desactivar dicho permiso desde la configuración correspondiente, entendiendo que algunas funciones podrían dejar de estar disponibles o funcionar de forma limitada.

El usuario es responsable de utilizar estas funciones en entornos adecuados y de evitar compartir por voz información confidencial, datos personales sensibles, datos de pago, contraseñas o conversaciones de terceros sin autorización. XIMA no será responsable por sonidos, voces o información captados por el dispositivo del usuario como resultado de la activación voluntaria de estas funciones.

En la máxima medida permitida por la ley, XIMA no garantiza la disponibilidad, precisión, interpretación correcta, continuidad, seguridad absoluta ni ausencia de errores en el reconocimiento, transmisión, procesamiento o reproducción de audio. XIMA no será responsable por comandos mal interpretados, respuestas automáticas, fallos técnicos, interrupciones, permisos del navegador o del dispositivo, interferencias, calidad del micrófono, retrasos, pérdidas de audio o consecuencias derivadas del uso o imposibilidad de uso de estas funciones.

Cuando estas funciones estén disponibles, el audio y las señales de voz podrán ser tratados técnica y temporalmente para ejecutar comandos, transmitir comunicación de voz, generar respuestas del juego o mejorar la experiencia, seguridad, calidad y funcionamiento del servicio, de acuerdo con la Política de Privacidad aplicable.

## 15. Terminación del Servicio

XIMA podrá cancelar, suspender o terminar total o parcialmente cualquier servicio, modalidad, cuenta, función o sistema interno cuando resulte necesario por razones técnicas, legales, comerciales, de seguridad o de funcionamiento.

La cancelación o suspensión del servicio no generará derecho a compensación, indemnización o reclamación económica.

## 16. Legislación Aplicable

Estos Términos y Condiciones se regirán por la legislación aplicable del lugar de residencia o establecimiento del titular responsable del proyecto XIMA, sin perjuicio de las normas obligatorias de protección al consumidor que puedan corresponder según la jurisdicción del usuario.

## 17. Contacto

Las consultas relacionadas con estos Términos y Condiciones podrán realizarse mediante los canales oficiales publicados en:

* **https://juegoxima.com**
* **https://playxima.com**

Al utilizar XIMA, el usuario reconoce haber leído, comprendido y aceptado estos Términos y Condiciones de Uso.`,
  en: `# XIMA TERMS AND CONDITIONS OF USE

**Last Updated:** [Date]

These Terms and Conditions govern the access to and use of the digital strategy game **XIMA**, available through its official websites:

* https://juegoxima.com
* https://playxima.com

By creating an account, accessing, or using any of these websites, you acknowledge that you have read, understood, and agreed to be bound by these Terms and Conditions.

---

# 1. General Information

XIMA is a digital strategy and entertainment game developed under the registered trademark **MUIÑO**.

The domains **juegoxima.com** and **playxima.com** are the official websites of the XIMA project.

The service is continuously evolving and may be under active development. As a result, certain features may change, be temporarily unavailable, or contain technical errors.

---

# 2. Use of the Service

The use of XIMA is voluntary and at the user's own risk.

The service is provided **"AS IS"**, without any express or implied warranties, including but not limited to:

* continuous availability;
* uninterrupted operation;
* error-free functionality;
* compatibility with all devices;
* permanent preservation of data, game progress, or matches.

To the fullest extent permitted by applicable law, the operators of XIMA shall not be liable for any loss of data, game progress, matches, service interruptions, indirect damages, or any consequences arising from the use or inability to use the service.

---

# 3. User Accounts

A user account is required to access the features of XIMA.

Users agree to provide accurate, complete, and up-to-date information during registration.

Users are responsible for maintaining the confidentiality of their login credentials and for all activities carried out through their accounts.

XIMA may require additional verification procedures before granting access to certain features, events, promotional rewards, or special services.

---

# 4. User Conduct

Users agree to use XIMA in a lawful, respectful, and responsible manner.

The following activities are strictly prohibited:

* using cheats, hacks, or unauthorized software;
* using unauthorized bots or automation tools;
* manipulating the operation of the game;
* exploiting software bugs or glitches;
* attempting to compromise the security of the platform;
* accessing restricted areas without authorization;
* reverse engineering the software;
* creating fraudulent or multiple accounts;
* impersonating other users;
* harassing, threatening, or disrupting other players.

---

# 5. Security Measures

XIMA reserves the right to suspend, restrict, or permanently terminate accounts, cancel matches, revoke rewards, block access, or take any other appropriate action whenever fraud, abuse, suspicious activity, or violations of these Terms are detected.

Such measures may be taken without prior notice whenever necessary to protect the security, integrity, and proper operation of the platform.

---

# 6. Nature of the Game

XIMA is a strategy, logic, and skill-based game.

The platform may include:

* multiplayer matches;
* tournaments;
* challenges;
* puzzles;
* practice modes;
* sandbox modes.

Some game modes may include random elements, such as dice rolls or similar mechanics.

These random elements are intended solely to create dynamic strategic scenarios and do not automatically determine the outcome of the game.

Success within XIMA depends primarily on the player's strategic thinking, planning, reasoning, experience, adaptability, and decision-making skills.

---

# 7. Virtual Currency

XIMA may include virtual currency, points, credits, experience, bonuses, and other digital items used exclusively within the game's ecosystem.

These virtual items:

* are not real money;
* are not electronic money;
* are not cryptocurrencies;
* are not financial assets;
* are not investments;
* have no monetary value outside XIMA;
* cannot be redeemed for cash;
* cannot be transferred to bank accounts.

They are intended solely for gameplay and progression within the platform.

---

# 8. Promotional Rewards

XIMA may occasionally offer limited promotional rewards, including official merchandise, promotional items, gift cards, physical prizes, digital content, or other special benefits.

These rewards are offered solely for promotional and entertainment purposes.

They do not constitute salaries, investments, financial assets, gambling winnings, or guaranteed monetary compensation.

Rewards are subject to:

* availability;
* account verification;
* anti-fraud validation;
* geographic restrictions;
* internal policies;
* compliance with these Terms.

Only eligible registered accounts that meet all applicable requirements may receive promotional rewards.

XIMA reserves the right to approve, reject, modify, suspend, or cancel any promotional reward, virtual balance, request, or user account in cases of fraud, abuse, suspicious activity, multiple accounts, unauthorized automation, bug exploitation, or violations of these Terms.

---

# 9. No Gambling

XIMA is not a casino, betting platform, lottery, or real-money gambling service.

Users do not wager or risk real money while playing.

Losing a match, challenge, tournament, or puzzle does not result in any financial loss.

Any promotional rewards offered by XIMA are part of entertainment and promotional activities only.

---

# 10. Intellectual Property

All content available within XIMA, including but not limited to:

* source code;
* game mechanics;
* rules;
* artwork;
* interfaces;
* 3D models;
* animations;
* music;
* sound effects;
* videos;
* documentation;
* books;
* databases;
* trademarks;
* logos;
* trade names;
* visual assets;

is protected by applicable copyright and intellectual property laws.

No content may be copied, reproduced, distributed, modified, extracted, commercialized, or otherwise used without prior written authorization from the rights holder.

---

# 11. Trademark

The **MUIÑO** trademark and all distinctive signs associated with XIMA are legally protected.

Any unauthorized reproduction, distribution, modification, commercial use, or exploitation is strictly prohibited without prior written permission from the rights holder.

---

# 12. Service Availability

XIMA may temporarily or permanently suspend all or part of the service due to maintenance, updates, technical issues, security concerns, force majeure, or any other circumstance necessary to ensure the proper operation of the platform.

Continuous availability of the service is not guaranteed.

---

# 13. Changes to the Terms

XIMA reserves the right to modify these Terms and Conditions at any time.

The platform, its rules, game modes, virtual economy, rewards, features, design, or content may also be updated or modified without prior notice.

Any changes become effective immediately upon publication on the official websites.

Continued use of XIMA after such changes constitutes acceptance of the revised Terms.

---

# 14. Privacy

Using XIMA may involve the processing of information necessary for the operation of the service, including registration data, gameplay statistics, technical identifiers, IP addresses, activity logs, security information, customer support records, and fraud prevention data.

Such information will be handled in accordance with the Privacy Policy published on the official websites.

### Microphone, Audio, and Voice Features

XIMA may request access to the microphone and process audio, voice commands, transcriptions, or voice signals only when the user enables or permits features that require them, including voice control, game responses, voice chat or communication, accessibility, improvements to the gameplay experience, and general operation of the website.

Microphone access depends on permission granted by the user through their browser, operating system, or device. The user may deny, withdraw, or disable that permission through the applicable settings, understanding that some features may become unavailable or function in a limited manner.

The user is responsible for using these features in appropriate environments and for avoiding the verbal disclosure of confidential information, sensitive personal data, payment information, passwords, or third-party conversations without authorization. XIMA shall not be responsible for sounds, voices, or information captured by the user's device as a result of the user's voluntary activation of these features.

To the fullest extent permitted by law, XIMA does not guarantee the availability, accuracy, correct interpretation, continuity, absolute security, or error-free operation of audio recognition, transmission, processing, or playback. XIMA shall not be liable for misinterpreted commands, automatic responses, technical failures, interruptions, browser or device permissions, interference, microphone quality, delays, audio loss, or any consequences arising from the use or inability to use these features.

Where these features are available, audio and voice signals may be processed technically and temporarily to execute commands, transmit voice communication, generate game responses, or improve the experience, security, quality, and operation of the service, in accordance with the applicable Privacy Policy.

---

# 15. Termination of the Service

XIMA reserves the right to suspend or permanently discontinue any feature, game mode, account, service, or functionality for technical, legal, commercial, security, or operational reasons.

The suspension or termination of the service does not create any right to compensation or financial reimbursement.

---

# 16. Governing Law

These Terms and Conditions shall be governed by the laws applicable in the jurisdiction of the owner or operator of the XIMA project, without prejudice to any mandatory consumer protection laws applicable in the user's jurisdiction.

---

# 17. Contact

Questions regarding these Terms and Conditions may be submitted through the official communication channels available on:

* https://juegoxima.com
* https://playxima.com

By using XIMA, you acknowledge that you have read, understood, and agreed to these Terms and Conditions of Use.`,
  ru: `# УСЛОВИЯ ИСПОЛЬЗОВАНИЯ XIMA

**Последнее обновление:** [Дата]

Настоящие Условия и положения регулируют доступ к цифровой стратегической игре **XIMA** и ее использование, доступной через официальные веб-сайты:

* https://juegoxima.com
* https://playxima.com

Создавая учетную запись, получая доступ или используя любой из этих веб-сайтов, вы подтверждаете, что прочитали, поняли и согласны соблюдать настоящие Условия и положения.

---

#1. Общая информация

XIMA — это цифровая стратегическая и развлекательная игра, разработанная под зарегистрированным товарным знаком **MUIÑO**.

Домены **juegoxima.com** и **playxima.com** являются официальными веб-сайтами проекта XIMA.

Сервис постоянно развивается и может находиться в активной разработке. В результате некоторые функции могут изменяться, быть временно недоступны или содержать технические ошибки.

---

#2. Использование сервиса

Использование XIMA является добровольным и осуществляется на собственный риск пользователя.

Сервис предоставляется «КАК ЕСТЬ», без каких-либо явных или подразумеваемых гарантий, включая, помимо прочего:

* постоянную доступность;
* бесперебойную работу;
* безошибочную функциональность;
* совместимость со всеми устройствами;

* постоянное сохранение данных, игрового прогресса или матчей.

В максимально допустимой степени в соответствии с действующим законодательством операторы XIMA не несут ответственности за потерю данных, игрового прогресса, матчей, перебои в работе сервиса, косвенные убытки или любые последствия, возникающие в результате использования или невозможности использования сервиса.

---

#3. Учетные записи пользователей

Для доступа к функциям XIMA требуется учетная запись пользователя.

Пользователи обязуются предоставлять точную, полную и актуальную информацию при регистрации.

Пользователи несут ответственность за сохранение конфиденциальности своих учетных данных и за все действия, совершаемые через их учетные записи.

XIMA может потребовать дополнительных процедур проверки перед предоставлением доступа к определенным функциям, событиям, рекламным наградам или специальным услугам.

--

#4. Правила поведения пользователей

Пользователи обязуются использовать XIMA законным, уважительным и ответственным образом.

Следующие действия строго запрещены:

* использование читов, взломов или несанкционированного программного обеспечения;
* использование несанкционированных ботов или инструментов автоматизации;

* манипулирование работой игры;

* использование программных ошибок или сбоев;

* попытка скомпрометировать безопасность платформы;

* несанкционированный доступ к ограниченным зонам;

* обратное проектирование программного обеспечения;

* создание мошеннических или множественных учетных записей;

* выдача себя за других пользователей;

* преследование, угрозы или нарушение порядка других игроков.

---

#5. Меры безопасности

XIMA оставляет за собой право приостанавливать, ограничивать или навсегда блокировать учетные записи, отменять матчи, отзывать награды, блокировать доступ или предпринимать любые другие соответствующие действия в случае обнаружения мошенничества, злоупотреблений, подозрительной активности или нарушений настоящих Условий.

Такие меры могут быть приняты без предварительного уведомления, когда это необходимо для защиты безопасности, целостности и надлежащего функционирования платформы.

---

#6. Характер игры

XIMA — это стратегическая, логическая и основанная на навыках игра.

Платформа может включать в себя:

* многопользовательские матчи;

* турниры;
* испытания;
* головоломки;
* режимы тренировки;
* режимы песочницы.

Некоторые игровые режимы могут включать случайные элементы, такие как броски кубиков или аналогичные механики.

Эти случайные элементы предназначены исключительно для создания динамичных стратегических сценариев и не определяют автоматически исход игры.

Успех в XIMA в первую очередь зависит от стратегического мышления игрока, планирования, логического мышления, опыта, адаптивности и навыков принятия решений.

--

#7. Виртуальная валюта

XIMA может включать виртуальную валюту, очки, кредиты, опыт, бонусы и другие цифровые предметы, используемые исключительно в экосистеме игры.

Эти виртуальные предметы:

* не являются реальными деньгами;
* не являются электронными деньгами;
* не являются криптовалютами;
* не являются финансовыми активами;
* не являются инвестициями;
* не имеют денежной ценности за пределами XIMA;

* не могут быть обменяны на наличные;

* не могут быть переведены на банковские счета.

Они предназначены исключительно для игрового процесса и прогресса на платформе.

--

#8. Рекламные награды

XIMA может время от времени предлагать ограниченные рекламные награды, включая официальную продукцию, рекламные товары, подарочные карты, физические призы, цифровой контент или другие специальные преимущества.

Эти награды предлагаются исключительно в рекламных и развлекательных целях. Они не являются заработной платой, инвестициями, финансовыми активами, выигрышами в азартных играх или гарантированной денежной компенсацией.

Награды предоставляются при соблюдении следующих условий:

* наличие;

* проверка учетной записи;

* проверка на предмет мошенничества;

* географические ограничения;

* внутренние правила;

* соответствие настоящим Условиям.

Только соответствующие зарегистрированные учетные записи, отвечающие всем применимым требованиям, могут получать рекламные награды.

XIMA оставляет за собой право утверждать, отклонять, изменять, приостанавливать или отменять любые рекламные награды, виртуальные балансы, запросы или учетные записи пользователей в случаях мошенничества, злоупотреблений, подозрительной активности, наличия нескольких учетных записей, несанкционированной автоматизации, использования ошибок или нарушений настоящих Условий.

---

#9. Использование микрофона, аудио и голосовых функций

XIMA может запрашивать доступ к микрофону и обрабатывать аудио, голосовые команды, транскрипции или голосовые сигналы только тогда, когда пользователь включает или разрешает функции, которым это необходимо, включая голосовое управление, ответы игры, голосовой чат или связь, функции доступности, улучшение игрового опыта и общее функционирование веб-сайта.

Доступ к микрофону зависит от разрешения, предоставленного пользователем в браузере, операционной системе или на устройстве. Пользователь может отказать в таком разрешении, отозвать его или отключить через соответствующие настройки, понимая, что некоторые функции могут стать недоступными или работать ограниченно.

Пользователь несет ответственность за использование этих функций в подходящей обстановке и за то, чтобы не раскрывать голосом конфиденциальную информацию, чувствительные персональные данные, платежные данные, пароли или разговоры третьих лиц без разрешения. XIMA не несет ответственности за звуки, голоса или информацию, захваченные устройством пользователя в результате добровольной активации этих функций пользователем.

В максимально допустимой законом степени XIMA не гарантирует доступность, точность, правильную интерпретацию, непрерывность, абсолютную безопасность или отсутствие ошибок при распознавании, передаче, обработке или воспроизведении аудио. XIMA не несет ответственности за неверно истолкованные команды, автоматические ответы, технические сбои, перебои, разрешения браузера или устройства, помехи, качество микрофона, задержки, потерю аудио или любые последствия, возникающие в результате использования или невозможности использования этих функций.

Когда такие функции доступны, аудио и голосовые сигналы могут технически и временно обрабатываться для выполнения команд, передачи голосовой связи, формирования ответов игры или улучшения опыта, безопасности, качества и функционирования сервиса в соответствии с применимой Политикой конфиденциальности.`,
  de: `# XIMA NUTZUNGSBEDINGUNGEN

**Letzte Aktualisierung:** [Datum]

Diese Nutzungsbedingungen regeln den Zugriff auf und die Nutzung des digitalen Strategiespiels **XIMA**, das über die offiziellen Websites verfügbar ist:

* https://juegoxima.com

* https://playxima.com

Durch die Erstellung eines Kontos, den Zugriff auf oder die Nutzung einer dieser Websites bestätigen Sie, dass Sie diese Nutzungsbedingungen gelesen, verstanden und akzeptiert haben.

---

#1. Allgemeine Informationen

XIMA ist ein digitales Strategie- und Unterhaltungsspiel, das unter der eingetragenen Marke **MUIÑO** entwickelt wurde.

Die Domains **juegoxima.com** und **playxima.com** sind die offiziellen Websites des XIMA-Projekts.

Der Dienst wird kontinuierlich weiterentwickelt und befindet sich möglicherweise noch in der aktiven Entwicklungsphase. Daher können sich bestimmte Funktionen ändern, vorübergehend nicht verfügbar sein oder technische Fehler enthalten.

---

#2. Nutzung des Dienstes

Die Nutzung von XIMA erfolgt freiwillig und auf eigenes Risiko.

Der Dienst wird **wie besehen** bereitgestellt, ohne jegliche ausdrückliche oder stillschweigende Gewährleistung. Dies schließt insbesondere Folgendes ein:

* ständige Verfügbarkeit;

* ununterbrochener Betrieb;

* fehlerfreie Funktionalität;

* Kompatibilität mit allen Geräten;

* dauerhafte Speicherung von Daten, Spielfortschritten oder Matches.

Soweit gesetzlich zulässig, haften die Betreiber von XIMA nicht für Datenverlust, Verlust von Spielfortschritten oder Matches, Dienstunterbrechungen, indirekte Schäden oder sonstige Folgen, die aus der Nutzung oder der Unmöglichkeit der Nutzung des Dienstes entstehen.

---

#3. Benutzerkonten

Für den Zugriff auf die Funktionen von XIMA ist ein Benutzerkonto erforderlich.

Benutzer verpflichten sich, bei der Registrierung korrekte, vollständige und aktuelle Informationen anzugeben.

Benutzer sind für die Geheimhaltung ihrer Zugangsdaten und für alle Aktivitäten verantwortlich, die über ihr Konto durchgeführt werden.

XIMA kann zusätzliche Verifizierungsverfahren durchführen, bevor der Zugriff auf bestimmte Funktionen, Events, Werbeaktionen oder spezielle Services gewährt wird.

---

#4. Nutzerverhalten

Nutzer verpflichten sich, XIMA rechtmäßig, respektvoll und verantwortungsbewusst zu nutzen.

Folgende Aktivitäten sind strengstens verboten:

* Verwendung von Cheats, Hacks oder unautorisierter Software;

* Verwendung unautorisierter Bots oder Automatisierungstools;

* Manipulation des Spielablaufs;

* Ausnutzen von Softwarefehlern oder -störungen;

* Versuch, die Sicherheit der Plattform zu gefährden;

* Unbefugter Zugriff auf gesperrte Bereiche;

* Reverse Engineering der Software;

* Erstellung betrügerischer oder mehrerer Accounts;

* Vortäuschung der Identität anderer Nutzer;

* Belästigung, Bedrohung oder Störung anderer Spieler.

---

#5. Sicherheitsmaßnahmen

XIMA behält sich das Recht vor, Konten zu sperren, einzuschränken oder dauerhaft zu kündigen, Spiele abzubrechen, Belohnungen zu widerrufen, den Zugriff zu blockieren oder andere geeignete Maßnahmen zu ergreifen, wenn Betrug, Missbrauch, verdächtige Aktivitäten oder Verstöße gegen diese Nutzungsbedingungen festgestellt werden.

Diese Maßnahmen können ohne vorherige Ankündigung ergriffen werden, wenn dies zum Schutz der Sicherheit, Integrität und des ordnungsgemäßen Betriebs der Plattform erforderlich ist.

---

#6. Spielprinzip

XIMA ist ein Strategie-, Logik- und Geschicklichkeitsspiel.

Die Plattform kann Folgendes umfassen:

* Mehrspieler-Matches;

* Turniere;

* Herausforderungen;

* Rätsel;

* Übungsmodi;

* Sandbox-Modi.

Einige Spielmodi können Zufallselemente wie Würfelwürfe oder ähnliche Mechanismen enthalten.

Diese Zufallselemente dienen ausschließlich der Erstellung dynamischer strategischer Szenarien und bestimmen nicht automatisch den Spielausgang.

Der Erfolg in XIMA hängt primär vom strategischen Denken, der Planung, dem logischen Schlussfolgern, der Erfahrung, der Anpassungsfähigkeit und den Entscheidungsfähigkeiten des Spielers ab.

---

#7. Virtuelle Währung

XIMA kann virtuelle Währung, Punkte, Credits, Erfahrungspunkte, Boni und andere digitale Gegenstände enthalten, die ausschließlich innerhalb des Spielökosystems verwendet werden.

Diese virtuellen Gegenstände:

* sind kein echtes Geld;

* sind kein elektronisches Geld;

* sind keine Kryptowährungen;

* sind keine Finanzanlagen;

* sind keine Investitionen;

* haben außerhalb von XIMA keinen Geldwert;

* können nicht gegen Bargeld eingelöst werden;

* können nicht auf Bankkonten überwiesen werden.

Sie sind ausschließlich für das Gameplay und den Spielfortschritt auf der Plattform gedacht.

---

#8. Werbeaktionen

XIMA bietet gelegentlich zeitlich begrenzte Werbeaktionen an, darunter offizielle Merchandise-Artikel, Werbegeschenke, Geschenkkarten, Sachpreise, digitale Inhalte oder andere besondere Vorteile.

Diese Belohnungen werden ausschließlich zu Werbe- und Unterhaltungszwecken angeboten.

Sie stellen weder Gehälter, Investitionen, Finanzanlagen, Glücksspielgewinne noch garantierte Geldleistungen dar.

Prämien unterliegen folgenden Bedingungen:

* Verfügbarkeit;

* Kontoverifizierung;

* Betrugsprüfung;

* geografische Beschränkungen;

* interne Richtlinien;

* Einhaltung dieser Bedingungen.

Nur berechtigte registrierte Konten, die alle geltenden Anforderungen erfüllen, können Aktionsprämien erhalten.

XIMA behält sich das Recht vor, Aktionsprämien, virtuelle Guthaben, Anfragen oder Benutzerkonten im Falle von Betrug, Missbrauch, verdächtigen Aktivitäten, Mehrfachkonten, unautorisierter Automatisierung, Ausnutzung von Sicherheitslücken oder Verstößen gegen diese Bedingungen zu genehmigen, abzulehnen, zu ändern, zu sperren oder zu stornieren.

---

#9. Nutzung von Mikrofon, Audio und Sprachfunktionen

XIMA kann Zugriff auf das Mikrofon anfordern und Audio, Sprachbefehle, Transkriptionen oder Sprachsignale nur dann verarbeiten, wenn der Nutzer Funktionen aktiviert oder erlaubt, die dies erfordern, einschließlich Sprachsteuerung, Spielantworten, Sprachchat oder Sprachkommunikation, Barrierefreiheit, Verbesserungen des Spielerlebnisses und des allgemeinen Betriebs der Website.

Der Zugriff auf das Mikrofon hängt von der Erlaubnis ab, die der Nutzer im Browser, Betriebssystem oder auf dem Gerät erteilt. Der Nutzer kann diese Erlaubnis über die jeweiligen Einstellungen verweigern, widerrufen oder deaktivieren und versteht, dass einige Funktionen dann möglicherweise nicht verfügbar sind oder nur eingeschränkt funktionieren.

Der Nutzer ist dafür verantwortlich, diese Funktionen in geeigneten Umgebungen zu verwenden und keine vertraulichen Informationen, sensiblen personenbezogenen Daten, Zahlungsdaten, Passwörter oder Gespräche Dritter ohne Erlaubnis mündlich offenzulegen. XIMA haftet nicht für Geräusche, Stimmen oder Informationen, die durch das Gerät des Nutzers infolge der freiwilligen Aktivierung dieser Funktionen erfasst werden.

Soweit gesetzlich zulässig, garantiert XIMA nicht die Verfügbarkeit, Genauigkeit, richtige Interpretation, Kontinuität, absolute Sicherheit oder Fehlerfreiheit bei der Erkennung, Übertragung, Verarbeitung oder Wiedergabe von Audio. XIMA haftet nicht für falsch interpretierte Befehle, automatische Antworten, technische Fehler, Unterbrechungen, Browser- oder Geräteberechtigungen, Störungen, Mikrofonqualität, Verzögerungen, Audioverlust oder Folgen, die sich aus der Nutzung oder Unmöglichkeit der Nutzung dieser Funktionen ergeben.

Wenn diese Funktionen verfügbar sind, können Audio- und Sprachsignale technisch und vorübergehend verarbeitet werden, um Befehle auszuführen, Sprachkommunikation zu übertragen, Spielantworten zu erzeugen oder die Erfahrung, Sicherheit, Qualität und Funktionsweise des Dienstes gemäß der geltenden Datenschutzrichtlinie zu verbessern.`,
};

export const TERMS_CONTENT_PARAGRAPHS = Object.fromEntries(
  Object.entries(TERMS_CONTENT_MARKDOWN).map(([language, markdown]) => [
    language,
    String(markdown || "")
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean),
  ])
);
